import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaArrowRight,
  FaBolt,
  FaCheckCircle,
  FaHeart,
  FaQuoteLeft,
  FaShareSquare,
  FaShieldAlt,
  FaThLarge,
  FaUsers,
  FaVideo
} from 'react-icons/fa';
import { setPageSEO } from '../utils/seo.js';
import { trackSignupFree } from '../utils/analytics.js';
import dashboardImage from '../assets/images/my-testimonials.png';
import './AboutUs.css';

const fadeInUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 }
};

const staggerChildren = {
  visible: {
    transition: {
      staggerChildren: 0.09
    }
  }
};

const seoMetadata = {
  title: 'About Clientalio – Customer Testimonial Software',
  description: 'Learn about Clientalio, a customer testimonial platform that helps businesses collect authentic video, text, and customer stories and turn them into trusted social proof.',
  keywords: 'Clientalio about us; testimonial software company; customer testimonial platform; video testimonial software; customer feedback software; social proof software',
  canonical: 'https://clientalio.com/about-us'
};

const whatWeDoCards = [
  {
    icon: <FaVideo aria-hidden="true" />,
    title: 'Video Testimonials',
    description: 'Customers record authentic video stories from any device — no app, no logins, no friction. Just real people sharing real results.'
  },
  {
    icon: <FaQuoteLeft aria-hidden="true" />,
    title: 'Text Testimonials',
    description: 'Collect written feedback, quotes, and customer stories through simple branded forms that feel effortless to answer.'
  },
  {
    icon: <FaThLarge aria-hidden="true" />,
    title: 'Screen-Recorded Stories',
    description: 'Capture walkthroughs, product reviews, and unboxing-style recordings straight from your customer\u2019s screen.'
  },
  {
    icon: <FaShareSquare aria-hidden="true" />,
    title: 'Social Proof Publishing',
    description: 'Turn customer stories into branded testimonial pages, Walls of Love, and website widgets that build trust where buyers decide.'
  }
];
const valuesCards = [
  {
    icon: <FaHeart aria-hidden="true" />,
    title: 'Authenticity First',
    description: 'Real customers, real words, real results. We never fabricate reviews — we make it easier for genuine customer love to be seen.'
  },
  {
    icon: <FaBolt aria-hidden="true" />,
    title: 'Simplicity Wins',
    description: 'Collecting proof should take minutes, not weeks. Every workflow is designed to remove steps, not add them.'
  },
  {
    icon: <FaUsers aria-hidden="true" />,
    title: 'Built for Every Business',
    description: 'Enterprise social proof tools shouldn\u2019t need an enterprise budget. Clientalio brings trusted proof within reach of every team.'
  },
  {
    icon: <FaShieldAlt aria-hidden="true" />,
    title: 'Trust by Design',
    description: 'Consent, ownership, and privacy are respected at every step — your customers always know how their stories are used.'
  }
];

const processSteps = [
  {
    step: '01',
    title: 'Collect',
    description: 'Share your branded request link. Customers record video, write feedback, or capture their screen in under two minutes.'
  },
  {
    step: '02',
    title: 'Manage',
    description: 'Review, organize, and approve every story from one dashboard. Tag favorites and keep your best proof ready to publish.'
  },
  {
    step: '03',
    title: 'Publish',
    description: 'Showcase stories on branded testimonial pages, Walls of Love, and website widgets — no developer required.'
  }
];

const statsItems = [
  { value: '2 min', label: 'To launch your first testimonial request' },
  { value: '3 formats', label: 'Video, text, and screen-recorded stories' },
  { value: '1 dashboard', label: 'To manage every customer story' },
  { value: '24/7', label: 'Social proof working on your website' }
];

function AnimatedSection({ children, className, id }) {
  return (
    <motion.section
      id={id}
      className={className}
      variants={fadeInUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.18 }}
      transition={{ duration: 0.55, ease: 'easeOut' }}
    >
      {children}
    </motion.section>
  );
}

function AboutUs() {
  useEffect(() => setPageSEO({
    title: seoMetadata.title,
    description: seoMetadata.description,
    keywords: seoMetadata.keywords,
    canonical: seoMetadata.canonical,
    ogTitle: seoMetadata.title,
    ogDescription: seoMetadata.description,
    twitterTitle: seoMetadata.title,
    twitterDescription: seoMetadata.description
  }), []);

  return (
    <main className="about-page">
      {/* Hero */}
      <section className="about-hero">
        <div className="container">
          <p className="about-eyebrow">About Clientalio</p>
          <h1>About Clientalio: Making Customer Proof Easier to Collect</h1>
          <p className="about-hero-lead">
            Clientalio is a customer testimonial platform that helps businesses collect
            authentic video, text, and customer stories — then turn them into trusted
            social proof that wins more buyers.
          </p>
          <div className="about-hero-actions">
            <Link
              to="/signup"
              className="btn-primary"
              onClick={() => trackSignupFree('about-hero')}
            >
              Start Collecting Free
            </Link>
            <Link to="/features" className="btn-secondary about-btn-secondary">
              Explore Features
            </Link>
          </div>
          <ul className="about-hero-trust">
            <li><FaCheckCircle aria-hidden="true" /> No credit card required</li>
            <li><FaCheckCircle aria-hidden="true" /> Set up in under 2 minutes</li>
            <li><FaCheckCircle aria-hidden="true" /> Branded testimonial pages</li>
          </ul>
        </div>
      </section>

      {/* Mission */}
      <AnimatedSection className="about-mission">
        <div className="container about-mission-inner">
          <p>
            Our mission is simple: make authentic customer proof so easy to collect that
            every business — not just big brands — can show real customer love and win
            trust before the first sales call.
          </p>
        </div>
      </AnimatedSection>

      {/* Stats */}
      <AnimatedSection className="about-stats">
        <div className="container about-stats-grid">
          {statsItems.map((item) => (
            <div className="about-stat" key={item.value}>
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </AnimatedSection>
      {/* What we do */}
      <AnimatedSection className="about-section about-what">
        <div className="container">
          <div className="about-section-header">
            <span className="about-eyebrow">What We Do</span>
            <h2>One Platform for Every Kind of Customer Proof</h2>
            <p>
              Clientalio brings testimonial collection, management, and publishing
              together so customer stories never get lost in inboxes and screenshots.
            </p>
          </div>
          <motion.div className="about-what-grid" variants={staggerChildren} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.18 }}>
            {whatWeDoCards.map((card) => (
              <motion.article className="about-what-card" key={card.title} variants={fadeInUp}>
                <span className="about-what-icon">{card.icon}</span>
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Story */}
      <AnimatedSection className="about-section about-story">
        <div className="container about-story-grid">
          <div className="about-story-copy">
            <span className="about-eyebrow">Our Story</span>
            <h2>Why We Built Clientalio</h2>
            <p>
              Every business works hard to earn customer love — yet most of it ends up
              buried in email threads, chat messages, and screenshots nobody sees again.
              Buyers want proof, and businesses have it. The missing piece was a simple
              way to connect the two.
            </p>
            <p>
              Clientalio started with a question: what if collecting a customer story was
              as easy as sending a link? Today, teams use Clientalio to request video and
              text testimonials in minutes, manage every story in one place, and publish
              trust-building social proof on the pages where buyers actually decide.
            </p>
            <p>
              From coaches and agencies to ecommerce stores and SaaS companies, businesses
              of every size use Clientalio to turn happy customers into their most
              convincing salespeople.
            </p>
          </div>
          <div className="about-story-visual">
            <img
              src={dashboardImage}
              alt="Clientalio dashboard for managing customer testimonials"
              loading="lazy"
            />
          </div>
        </div>
      </AnimatedSection>
      {/* Values */}
      <AnimatedSection className="about-section about-values">
        <div className="container">
          <div className="about-section-header">
            <span className="about-eyebrow">Our Values</span>
            <h2>The Principles Behind Every Feature</h2>
          </div>
          <motion.div className="about-values-grid" variants={staggerChildren} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.18 }}>
            {valuesCards.map((card) => (
              <motion.article className="about-value-card" key={card.title} variants={fadeInUp}>
                <span className="about-value-icon">{card.icon}</span>
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </AnimatedSection>

      {/* How it works */}
      <AnimatedSection className="about-section about-process">
        <div className="container">
          <div className="about-section-header">
            <span className="about-eyebrow">How It Works</span>
            <h2>From Request to Published Proof in Three Steps</h2>
          </div>
          <motion.ol className="about-process-grid" variants={staggerChildren} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.18 }}>
            {processSteps.map((item) => (
              <motion.li className="about-process-card" key={item.step} variants={fadeInUp}>
                <span className="about-process-step">{item.step}</span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </motion.li>
            ))}
          </motion.ol>
        </div>
      </AnimatedSection>

      {/* CTA */}
      <section className="cta-section about-cta">
        <div className="container">
          <h2>Ready to Make Customer Proof Easier to Collect?</h2>
          <p>
            Join the businesses using Clientalio to collect authentic customer stories and
            turn them into social proof that builds trust and drives conversions.
          </p>
          <Link
            to="/signup"
            className="btn-primary"
            onClick={() => trackSignupFree('about-cta')}
          >
            Start Free In Under 2 Minutes
            <FaArrowRight aria-hidden="true" />
          </Link>
          <p className="cta-trust">✓ No credit card required ✓ Branded pages ✓ Website-ready widgets</p>
        </div>
      </section>
    </main>
  );
}

export default AboutUs;